import { SectionHeader } from '../common/section-header'
import { Link } from 'react-router-dom'

import { Badge, Icon, Notice } from '@speaking-coach/ui'
import { ATTENTION_LABEL } from '../students/student-attention'
import type { AttentionReason } from '../students/types'
import type { CohortDetail, CohortRow } from './types'

function studentsUrl(
  cohort: Pick<CohortRow, 'clientId' | 'cohortId'>,
  filters: Record<string, string> = {},
): string {
  // The students page's cohort picker is scoped by client. Carry both parts of that
  // scope so the applied cohort remains visible (and removable) in the filter
  // rail instead of becoming a hidden URL-only filter.
  const search = new URLSearchParams({
    clientId: cohort.clientId,
    cohortId: cohort.cohortId,
    ...filters,
  })
  return `/admin/students?${search.toString()}`
}

function DetailMetric({
  count,
  label,
  to,
  attention = false,
  gridClassName = '',
}: {
  count: number
  label: string
  to?: string
  attention?: boolean
  gridClassName?: string
}): JSX.Element {
  const content = (
    <>
      <span
        className={
          attention && count > 0
            ? 'text-section-title font-semibold tabular-nums text-danger-text'
            : 'text-section-title font-semibold tabular-nums text-text-primary'
        }
      >
        {count}
      </span>
      <span className="text-caption text-text-secondary">{label}</span>
    </>
  )

  if (to === undefined || count === 0) {
    return (
      <div
        className={`flex min-w-0 flex-col gap-0.5 py-0.5 ${gridClassName}`}
      >
        {content}
      </div>
    )
  }

  return (
    <Link
      to={to}
      aria-label={`${count} ${label.toLowerCase()}`}
      className={`group -m-1.5 flex min-w-0 flex-col gap-0.5 rounded-token-input p-1.5 outline-none hover:bg-surface-app focus-visible:focus-ring ${gridClassName}`}
    >
      {content}
      <span className="inline-flex items-center gap-1 whitespace-nowrap text-caption font-medium text-text-primary group-hover:underline">
        View students
        <Icon name="goTo" size={12} />
      </span>
    </Link>
  )
}

function JourneyMetric({
  count,
  label,
}: {
  count: number
  label: string
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 py-0.5">
      <span className="block text-section-title font-semibold tabular-nums text-text-primary">
        {count}
      </span>
      <span className="text-caption text-text-secondary">{label}</span>
    </div>
  )
}

function JourneyConnector(): JSX.Element {
  return (
    <div aria-hidden className="flex h-8 items-center justify-center">
      <Icon name="forward" size={14} className="text-text-faint" />
    </div>
  )
}

/** Structured, actionable funnel hierarchy for one cohort's detail page. */
export function CohortDetailFunnel({
  cohort,
}: {
  cohort: CohortDetail
}): JSX.Element {
  const attentionReasons = (
    Object.entries(cohort.attentionReasonCounts) as [
      AttentionReason,
      number | undefined,
    ][]
  ).filter((entry): entry is [AttentionReason, number] => (entry[1] ?? 0) > 0)

  return (
    <div className="grid gap-4 xl:grid-cols-6 xl:gap-0">
      <div className="space-y-3 xl:col-span-3">
        <section aria-labelledby="cohort-summary-title" className="xl:pr-6">
          <SectionHeader
            level={3}
            id="cohort-summary-title"
            title="Cohort summary"
          />
          <div className="mt-1.5 grid grid-cols-3 gap-x-6 gap-y-2">
            <DetailMetric
              count={cohort.memberCount}
              label="Students"
              to={studentsUrl(cohort)}
            />
            <DetailMetric
              count={cohort.inviteIssued}
              label="Tracked invitations"
              gridClassName="col-span-2"
            />
          </div>
        </section>

        <section
          aria-labelledby="learning-journey-title"
          className="border-t border-border-default pb-3 pt-3"
        >
          <div className="xl:pr-6">
            <SectionHeader
              level={3}
              id="learning-journey-title"
              title="Learning journey"
            />
            <div className="mt-1.5 grid grid-cols-[minmax(0,1fr)_1rem_minmax(0,1fr)_1rem_minmax(0,1fr)] gap-x-2 sm:gap-x-3">
              <JourneyMetric count={cohort.activated} label="Activated" />
              <JourneyConnector />
              <JourneyMetric count={cohort.placed} label="Placed" />
              <JourneyConnector />
              <JourneyMetric count={cohort.learning} label="Started learning" />
            </div>
          </div>
        </section>
      </div>

      <section
        aria-labelledby="cohort-attention-title"
        className="border-t border-border-default pt-4 xl:col-span-3 xl:border-l xl:border-t-0 xl:pl-6 xl:pt-0"
      >
        <SectionHeader
          level={3}
          id="cohort-attention-title"
          title="Attention"
        />
        <div className="mt-1.5 grid grid-cols-3 gap-x-6 gap-y-2">
          <DetailMetric
            count={cohort.needsAttention}
            label="Needs attention"
            to={studentsUrl(cohort, { needsAttention: 'true' })}
            attention
          />
          <DetailMetric
            count={cohort.invitesExpiring}
            label="Invites expiring"
            to={studentsUrl(cohort, { attentionReason: 'invite_expiring' })}
            attention
          />
          <DetailMetric
            count={cohort.invitesExpired}
            label="Invites expired"
            to={studentsUrl(cohort, { attentionReason: 'invite_expired' })}
            attention
          />
        </div>

        {attentionReasons.length > 0 && (
          <div
            className="mt-3 flex flex-wrap gap-1.5"
            aria-label="Attention reasons"
          >
            {attentionReasons.map(([reason, count]) => (
              <Link
                key={reason}
                to={studentsUrl(cohort, { attentionReason: reason })}
                aria-label={`View ${count} students: ${ATTENTION_LABEL[reason]}`}
                className="rounded-token-input outline-none focus-visible:focus-ring"
              >
                <Badge variant="danger">
                  {count} {ATTENTION_LABEL[reason]}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </section>

      {cohort.warnings.some(
        (warning) => warning.scope === 'student_funnel',
      ) && (
        <div className="space-y-2 border-t border-border-default pt-3 xl:col-span-6">
          {cohort.warnings
            .filter((warning) => warning.scope === 'student_funnel')
            .map((warning) => (
              <Notice
                key={warning.message}
                variant="warning"
                textSize="caption"
              >
                {warning.message}
              </Notice>
            ))}
        </div>
      )}
    </div>
  )
}

/** Compact, zero-suppressing facts for a cohort-list table cell. */
export function CohortListFunnel({
  cohort,
}: {
  cohort: CohortRow
}): JSX.Element {
  const entries = [
    { count: cohort.inviteIssued, label: 'tracked invitations' },
    { count: cohort.activated, label: 'activated' },
    { count: cohort.placed, label: 'placed' },
    { count: cohort.learning, label: 'learning' },
    {
      count: cohort.needsAttention,
      label: 'needs attention',
      attention: true,
    },
    { count: cohort.invitesExpiring, label: 'expiring', attention: true },
    { count: cohort.invitesExpired, label: 'expired', attention: true },
  ].filter((entry) => entry.count > 0)

  if (entries.length === 0) {
    return (
      <span
        className="text-text-secondary"
        aria-label={`No funnel activity for ${cohort.name}`}
      >
        —
      </span>
    )
  }

  return (
    <ul
      aria-label={`Funnel counts for ${cohort.name}`}
      className="flex max-w-sm flex-wrap gap-x-3 gap-y-1 text-caption text-text-secondary"
    >
      {entries.map((entry) => (
        <li
          key={entry.label}
          className={entry.attention ? 'text-danger-text' : undefined}
        >
          <span className="font-semibold tabular-nums text-current">
            {entry.count}
          </span>{' '}
          {entry.label}
        </li>
      ))}
    </ul>
  )
}
